import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { Dropdown } from 'antd';
import { AgGridReact } from 'ag-grid-react';
import AgInputEditor from './AgInputEditor';
import AgRichEditor from './AgRichEditor';
import {
  getExecutiveSummaryField,
  getExecutiveSummaryData,
  setExecutiveSummaryField,
  setExecutiveSummaryValue,
} from '@service/apis/projects.api';

const StatusMapping = {
  1: 'Not Started',
  2: 'On Track',
  3: 'Delayed',
  4: 'At Risk',
  5: 'Completed',
};

const suppressShiftEnterEvent = (params) => {
  const isShiftKey = params.event.shiftKey;
  const isEnterKey = params.event.keyCode === 13;
  if (isShiftKey && isEnterKey) {
    return true;
  }
  return false;
};

const ExecutiveSummary = (props) => {
  const gridRef = useRef();
  const gridStyle = useMemo(() => ({ height: 700 }), []);
  const [rowData, setRowData] = useState([]);

  const operationSave = () => {
    const { rowsToDisplay = [] } = gridRef.current.api.rowModel || {};
    const rows = rowsToDisplay.map((item) => item.data);

    setExecutiveSummaryValue(props.projectId, rows).then((res) => {
      console.log('save successfull');
    });
  };

  const handleAddRow = useCallback(() => {
    const newRow = { isCustomizedRow: true, status: 1, id: Date.now() };
    gridRef.current.api.applyTransaction({
      add: [newRow],
      addIndex: rowData.length - 1,
    });
    gridRef.current.api.ensureNodeVisible(newRow, 'bottom');
    operationSave();
  }, [rowData]);

  const handleRemoveRow = useCallback((id) => {
    gridRef.current.api.applyTransaction({
      remove: [{ id }],
    });
    operationSave();
  }, []);

  const AddRowComponent = () => (
    <div className="add-button" onClick={handleAddRow}>
      <span
        className="appkiticon icon-circle-plus-fill"
        style={{ marginRight: 6 }}
      />
      Add Row
    </div>
  );

  const removeCellRenderer = useCallback((params) => {
    if (!params.data.isCustomizedRow) {
      return null;
    }
    return (
      <div
        className="romove-button"
        onClick={() => handleRemoveRow(params.data.id)}
      >
        <span className="appkiticon icon-circle-delete-fill" />
      </div>
    );
  }, []);

  const updateItemStatus = (params, value) => {
    gridRef.current.api.applyTransaction({
      update: [{ ...params.data, status: value }],
    });

    operationSave();
  };

  const renderStatusOptionList = useCallback(
    (params) => (
      <div
        className="executive-status-options"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {Object.keys(StatusMapping).map((key, index) => (
          <div
            className="status-option-item"
            tabIndex={index}
            onClick={() => updateItemStatus(params, key)}
          >
            {StatusMapping[key]}
          </div>
        ))}
      </div>
    ),
    [],
  );

  const statusCellRenderer = useCallback((params) => {
    if (params.data.addRow) {
      return <AddRowComponent />;
    }

    if (!params.data.status) {
      return null;
    }
    const mapping = StatusMapping[params.data.status] || '';
    return (
      <Dropdown
        trigger={['click']}
        align={{ offset: [0, 10] }}
        overlay={renderStatusOptionList(params)}
        placement="bottom"
        overlayStyle={{ width: 150 }}
        getPopupContainer={(trigger) =>
          trigger.parentElement.parentElement.parentElement.parentElement
            .parentElement.parentElement
        }
      >
        <div className={`status-capsule status-capsule-${params.data.status}`}>
          {mapping}
        </div>
      </Dropdown>
    );
  }, []);

  const defaultCellRenderer = (params) => {
    if (params.data.addRow) {
      return <AddRowComponent />;
    }
    if (params.richText) {
      return (
        <div
          className="cell-text"
          dangerouslySetInnerHTML={{ __html: params.value }}
        />
      );
    }
    return <div className="cell-text">{params.value || null}</div>;
  };

  const defaultColumns = [
    {
      headerName: 'Function',
      field: 'function',
      cellEditor: AgInputEditor,
      cellRenderer: defaultCellRenderer,
    },
    {
      headerName: 'Owner',
      field: 'owner',
      cellEditor: AgInputEditor,
      cellRenderer: defaultCellRenderer,
    },
    {
      headerName: 'Status',
      field: 'status',
      editable: false,
      cellRenderer: statusCellRenderer,
    },
    {
      headerName: 'Key Highlights & Critical Open Items',
      field: 'keyHLAndCritOpenItems',
      cellRenderer: defaultCellRenderer,
      cellEditor: AgRichEditor,
      cellEditorPopup: true,
      cellRendererParams: {
        richText: true,
      },
      suppressKeyboardEvent: suppressShiftEnterEvent,
    },
    {
      headerName: 'Immediate Next Steps',
      field: 'ImmeNextSteps',
      resizable: false,
      cellRenderer: defaultCellRenderer,
      cellEditor: AgRichEditor,
      cellEditorPopup: true,
      cellRendererParams: {
        richText: true,
      },
      suppressKeyboardEvent: suppressShiftEnterEvent,
    },
    {
      headerName: '',
      field: 'action',
      width: 60,
      editable: false,
      resizable: false,
      suppressMovable: true,
      lockPosition: 'right',
      cellRenderer: removeCellRenderer,
    },
  ];

  const [columnDefs, setColumnDefs] = useState(defaultColumns);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      wrapText: true,
      autoHeight: true,
      editable: (params) => !params.data.addRow,
      colSpan: (params) => (params.data.addRow ? 6 : 1),
    }),
    [],
  );

  const onFirstDataRendered = (params) => {
    params.api.sizeColumnsToFit();
  };

  const fetchColumns = (id) => {
    getExecutiveSummaryField(id).then((res) => {
      if (res && res.fieldConfig && res.fieldConfig.length) {
        const fields = res.fieldConfig.map((item, index) => {
          const defaultField = defaultColumns.find(
            (v) => v.field === item.mapping,
          );
          return {
            ...defaultField,
            width: item.width,
            headerName: item.name,
            field: item.mapping,
            resizable: index + 1 === res.fieldConfig.length ? false : true,
          };
        });
        setColumnDefs([
          ...fields,
          {
            headerName: '',
            field: 'action',
            width: 60,
            editable: false,
            resizable: false,
            suppressMovable: true,
            lockPosition: 'right',
            cellRenderer: removeCellRenderer,
          },
        ]);
      }
    });
  };

  const fetchData = (id) => {
    getExecutiveSummaryData(id).then((res) => {
      if (
        res &&
        res.executiveOverviewData &&
        res.executiveOverviewData.length
      ) {
        const now = Date.now();
        const newData = res.executiveOverviewData.map((item, index) => ({
          ...item,
          status: item.status || 1,
          id: now + index,
        }));
        setRowData(newData);
      } else {
        setRowData(res.executiveOverviewData);
      }
    });
  };

  useEffect(() => {
    if (props.projectId) {
      fetchColumns(props.projectId);
      fetchData(props.projectId);
    }
  }, [props.projectId]);

  useEffect(() => {
    const resizeWindow = () => {
      gridRef.current &&
        gridRef.current.api &&
        gridRef.current.api.sizeColumnsToFit();
    };
    window.addEventListener('resize', resizeWindow);
    return () => {
      window.addEventListener('resize', resizeWindow);
    };
  }, []);

  const onDragStopped = useCallback(
    (params) => {
      const cols = gridRef.current.api.getColumnDefs();
      const newCols = cols.map((col, index) => ({
        ...col,
        resizable:
          cols.length === index + 2 || cols.length === index + 1 ? false : true,
      }));
      setColumnDefs(newCols);

      const submitFields = cols
        .filter((v) => v.field !== 'action')
        .map((col) => ({
          width: col.width || undefined,
          name: col.headerName,
          mapping: col.field,
        }));
      setExecutiveSummaryField(props.projectId, submitFields).then((res) => {
        console.log('save successfull');
      });
    },
    [props.projectId],
  );

  const onCellEditingStopped = useCallback(
    (params) => {
      if (!params || !params.valueChanged) {
        return;
      }
      setExecutiveSummaryValue(props.projectId, rowData).then((res) => {
        console.log('save successfull');
      });
    },
    [props.projectId, rowData],
  );

  const onCellKeyPress = useCallback((event) => {
    if (event.colDef && event.colDef.field === 'status') {
      const capasule = event.event?.target?.querySelector('.status-capsule');
      if (event.event && event.event.keyCode === 13) {
        if (capasule) {
          capasule.click && capasule.click();
        }
      } else {
        const dropdown =
          event.event?.target?.querySelector('.ant-dropdown-open');
        if (dropdown) {
          capasule.click && capasule.click();
        }
      }
    }
  }, []);

  return (
    <div className="ag-theme-alpine" style={gridStyle}>
      <AgGridReact
        ref={gridRef}
        getRowId={(params) => params.data.id}
        colResizeDefault="shift"
        rowBuffer={70}
        rowHeight={50}
        headerHeight={60}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        suppressDragLeaveHidesColumns={true}
        onFirstDataRendered={onFirstDataRendered}
        suppressColumnVirtualisation={true}
        suppressRowVirtualisation={true}
        onDragStopped={onDragStopped}
        pinnedBottomRowData={[{ addRow: true }]}
        onCellEditingStopped={onCellEditingStopped}
        onCellKeyDown={onCellKeyPress}
      />
    </div>
  );
};

export default ExecutiveSummary;
